/**
 * AuditHealQuotationsDialog
 *
 * Admin maintenance tool. Scans every saved quotation for the iter-104
 * "per-sqft collapse" bug (where the saved amount was stored as
 * rate × qty instead of rate × sqft × qty). Two-step flow:
 *
 *   1. "Run Audit (dry run)" → POST /admin/quotations/audit-heal?dry_run=true
 *      Shows a per-quotation sample of what would change.
 *   2. "Apply Heal" → POST /admin/quotations/audit-heal?dry_run=false
 *      Persists corrected amounts + subtotal + grand_total.
 *
 * Healing is reversible only via backup — show the warning prominently.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { api, formatINR } from '../../lib/store';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, CheckCircle2, AlertTriangle, BellRing } from 'lucide-react';

export default function AuditHealQuotationsDialog({ open, onOpenChange }) {
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(false);

  const runAudit = async () => {
    setRunning(true); setApplied(false);
    try {
      const res = await api.auditHealQuotations(true);
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const applyHeal = async () => {
    if (!window.confirm('This will rewrite amounts on all broken quotations in the database. Make sure you have a recent backup. Proceed?')) return;
    setApplying(true);
    try {
      const res = await api.auditHealQuotations(false);
      setResult(res.data);
      setApplied(true);
      toast.success(`Healed ${res.data.fixed_items} item(s) across ${res.data.broken_quotations} quotation(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setApplying(false);
    }
  };

  const reset = () => { setResult(null); setApplied(false); };

  const runNightlyNow = async () => {
    setRunning(true);
    try {
      const res = await api.runNightlyAuditNow();
      const r = res.data;
      const sw = Number(r.stale_walkins || 0);
      const sl = Number(r.stale_leads || 0);
      const bq = Number(r.broken_quotations || 0);
      if (bq + sw + sl === 0) {
        toast.success(`All ${r.scanned} quotations healthy ✅ — no stale follow-ups either.`);
      } else {
        const bits = [];
        if (bq > 0) bits.push(`${r.fixed_items} broken item(s) in ${bq} quotation(s)`);
        if (sw > 0) bits.push(`${sw} overdue walk-in(s)`);
        if (sl > 0) bits.push(`${sl} overdue lead(s)`);
        toast.success(`Notified ${r.notified} admin(s) — ${bits.join(', ')}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Watchdog failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="audit-heal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#DAA520]" /> Audit & Heal Quotations
          </DialogTitle>
          <p className="text-xs text-[#5C6150] mt-1">
            Scans every saved quotation for the per-sqft amount-collapse bug. Step 1 runs a dry-run preview; Step 2 writes the corrected amounts.
          </p>
          <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-[#F2F4EF] px-2 py-1.5">
            <span className="text-[10px] text-[#5C6150] flex items-center gap-1.5">
              <BellRing className="w-3 h-3 text-[#556B2F]" />
              Nightly health check at <strong>02:00 IST</strong>: broken amounts + overdue walk-ins / leads. Admins get a single WhatsApp digest if anything needs attention.
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] text-[#556B2F] hover:bg-[#556B2F]/10"
              onClick={runNightlyNow}
              disabled={running || applying}
              data-testid="audit-run-nightly-now-btn"
            >
              Test now
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {!result && !running && (
            <div className="border border-dashed border-[#E2E6D9] rounded-md p-6 text-center text-sm text-[#5C6150]">
              Click <strong>Run Audit (dry run)</strong> to scan all quotations. Nothing will be written.
            </div>
          )}

          {running && (
            <div className="flex items-center gap-2 text-sm text-[#5C6150] py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanning the entire quotations collection…
            </div>
          )}

          {result && (
            <>
              {/* Summary chips */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Quotations scanned" value={result.scanned} testId="audit-stat-scanned" />
                <Stat label="Broken quotations" value={result.broken_quotations} accent="amber" testId="audit-stat-broken" />
                <Stat label="Items to fix" value={result.fixed_items} accent="amber" testId="audit-stat-items" />
                <Stat
                  label="Net amount change"
                  value={formatINR(Math.round(result.total_amount_delta))}
                  accent={result.total_amount_delta >= 0 ? 'green' : 'red'}
                  testId="audit-stat-delta"
                />
              </div>

              {result.broken_quotations === 0 ? (
                <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-md p-4 text-sm text-[#1B7531] flex items-center gap-2" data-testid="audit-allclean">
                  <CheckCircle2 className="w-4 h-4" /> All quotations are healthy — nothing to heal.
                </div>
              ) : (
                <>
                  {applied ? (
                    <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-md p-3 text-sm text-[#1B7531] flex items-center gap-2" data-testid="audit-applied">
                      <CheckCircle2 className="w-4 h-4" /> Heal applied to {result.fixed_items} item(s) across {result.broken_quotations} quotation(s).
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Dry-run preview below.</p>
                        <p className="mt-1">The audit will ONLY heal items where: (a) category is a per-sqft category (Plywood, Veneers, Doors, MDF, WPC/PVC, Blockboard), (b) the saved <code className="bg-amber-100 px-1 rounded">amount</code> = <code className="bg-amber-100 px-1 rounded">rate × qty</code> (the exact &quot;sqft factor dropped&quot; signature), and (c) healing would only INCREASE the amount. Anything else is left for manual review via cart edit. We recommend a database backup before clicking Apply Heal.</p>
                      </div>
                    </div>
                  )}

                  {/* Sample table */}
                  <div className="border border-[#E2E6D9] rounded-md overflow-x-auto bg-white">
                    <table className="w-full text-xs min-w-[640px]">
                      <thead className="bg-[#F2F4EF] text-[#5C6150]">
                        <tr>
                          <th className="text-left py-2 px-3">Quotation</th>
                          <th className="text-left py-2 px-3">Customer</th>
                          <th className="text-left py-2 px-3">Item</th>
                          <th className="text-right py-2 px-3">Before</th>
                          <th className="text-right py-2 px-3">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.samples.map((s, i) => (
                          <tr key={i} className="border-t border-[#E2E6D9]" data-testid={`audit-sample-row-${i}`}>
                            <td className="py-2 px-3 font-mono-data">{s.quotation_number || s.quotation_id.slice(0, 8)}</td>
                            <td className="py-2 px-3">{s.customer_name || '—'}</td>
                            <td className="py-2 px-3 truncate max-w-[180px]" title={s.item_desc}>{s.item_desc}</td>
                            <td className="py-2 px-3 text-right font-mono-data text-red-700">{formatINR(Math.round(s.before_amount))}</td>
                            <td className="py-2 px-3 text-right font-mono-data text-[#1B7531] font-semibold">{formatINR(Math.round(s.after_amount))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {result.fixed_items > result.samples.length && (
                    <p className="text-[10px] text-[#8F9485] italic">
                      Showing {result.samples.length} of {result.fixed_items} items. All others will be healed the same way.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-[#E2E6D9] gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running || applying} data-testid="audit-close-btn">
            Close
          </Button>
          {!result || applied ? (
            <Button
              onClick={runAudit}
              disabled={running || applying}
              className="bg-[#556B2F] hover:bg-[#465926] text-white"
              data-testid="audit-run-btn"
            >
              {running ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Scanning…</> : 'Run Audit (dry run)'}
            </Button>
          ) : (
            result.broken_quotations > 0 && (
              <Button
                onClick={applyHeal}
                disabled={applying}
                className="bg-[#DAA520] hover:bg-[#B07700] text-white"
                data-testid="audit-apply-btn"
              >
                {applying ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Healing…</> : `Apply Heal (${result.fixed_items} items)`}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent, testId }) {
  const colours = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    green: 'bg-[#25D366]/10 border-[#25D366]/30 text-[#1B7531]',
    red: 'bg-red-50 border-red-200 text-red-800',
  };
  const cls = colours[accent] || 'bg-[#F2F4EF] border-[#E2E6D9] text-[#1A1C16]';
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-base font-bold font-mono-data mt-0.5">{value}</p>
    </div>
  );
}
